import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getPlanConfig } from '../../utils/membershipUtils';
import Pagination from '../../components/Pagination/Pagination';
import { API_BASE_URL, SOCKET_URL } from '../../config/api';
import './ProfessionalPage.css';

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${SOCKET_URL}${url}`;
  return url;
};

const ProfessionalPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [showCategoryDD, setShowCategoryDD] = useState(false);
  const [showSubCategoryDD, setShowSubCategoryDD] = useState(false);
  const [showCountryDD, setShowCountryDD] = useState(false);

  // API data
  const [professionals, setProfessionals] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterCategories, setFilterCategories] = useState([]);
  const [filterCountries, setFilterCountries] = useState([]);

  // Handle professional card click to navigate to profile
  const handleProfessionalClick = (professionalId) => {
    navigate(`/professional/${professionalId}`);
  };

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [catRes, countryRes] = await Promise.all([
          fetch(`${API_BASE_URL}/specialists/filters/categories`),
          fetch(`${API_BASE_URL}/specialists/filters/countries`)
        ]);
        if (catRes.ok) {
          const data = await catRes.json();
          setFilterCategories(data);
        }
        if (countryRes.ok) {
          const data = await countryRes.json();
          setFilterCountries(data);
        }
      } catch (error) {
        console.error('Error fetching specialist filters:', error);
      }
    };
    fetchFilters();
  }, []);

  // Fetch specialists
  const fetchSpecialists = useCallback(async (page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedCountry) params.append('country', selectedCountry);
      params.append('limit', ITEMS_PER_PAGE);
      params.append('offset', (page - 1) * ITEMS_PER_PAGE);

      const headers = {};
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_BASE_URL}/specialists?${params.toString()}`, { headers });
      if (res.ok) {
        const data = await res.json();
        // Map API data to match component expectations
        const mapped = (data.specialists || []).map(s => {
          const coverUrl = getImageUrl(s.coverImage) || getImageUrl(s.avatar) || '';
          // Generate text-based avatar from initials
          const initials = (s.name || 'S').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
          const colors = ['4F46E5','7C3AED','2563EB','0891B2','059669','D97706','DC2626','BE185D','7C2D12','1D4ED8','6D28D9','0D9488','CA8A04','9333EA','0284C7','DB2777','EA580C','4338CA','0E7490','B91C1C'];
          const colorIdx = (s.id || 0) % colors.length;
          const avatarGenUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(s.name || 'S')}&size=200&background=${colors[colorIdx]}&color=fff&bold=true&font-size=0.4`;
          return {
            id: s.id,
            title: s.title || 'Specialist',
            name: s.name,
            country: s.country || 'N/A',
            isGlobal: s.membershipPlan === 'enterprise' || s.membershipPlan === 'professional',
            isVerified: true,
            rating: 4.5,
            category: s.category || 'N/A',
            expertise: s.expertise || 'N/A',
            interestedLocations: s.interestedLocations || 'N/A',
            experience: s.experience || 'N/A',
            membershipPlan: s.membershipPlan,
            avatar: avatarGenUrl,
            galleryImage: coverUrl
          };
        });
        setProfessionals(mapped);
        setTotalResults(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching specialists:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedCountry]);

  // Fetch on mount and filter/page change
  useEffect(() => {
    fetchSpecialists(currentPage);
  }, [currentPage, fetchSpecialists]);

  const ProfessionalCard = ({ professional }) => (
    <div className="professional-card" onClick={() => handleProfessionalClick(professional.id)} style={{ cursor: 'pointer' }}>
      <div className="professional-card-content">
        {/* Avatar / Image */}
        <div className="professional-gallery">
          <div className="professional-gallery-wrapper">
            <img 
              src={professional.galleryImage || professional.avatar}
              alt={professional.name}
              className="professional-main-image"
              onError={(e) => { e.target.src = professional.avatar; }}
            />
          </div>
        </div>

        {/* Professional Info */}
        <div className="professional-info">
          {/* Header Section */}
          <div className="professional-header">
            <div className="professional-avatar">
              <img 
                src={professional.avatar} 
                alt={professional.name}
                className="avatar-image"
              />
            </div>
            <div className="professional-header-content">
              <div className="professional-title-section">
                <div className="professional-title-wrapper">
                  <h3 className="professional-title">{professional.title}</h3>
                </div>
              </div>
              <div className="professional-name-location">
                <span className="professional-name">{professional.name}</span>
                <span className="professional-separator"> |  </span>
                <span className="professional-location">{professional.country}</span>
              </div>
              <div className="professional-badges">
                <div className="professional-badges-row">
                  {(() => {
                    const planConfig = getPlanConfig(professional.membershipPlan);
                    const isGlobal = planConfig.planType === 'global';
                    return (
                      <div className="professional-global-badge" style={{ borderColor: planConfig.color }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                          {isGlobal ? (
                            <>
                              <circle cx="12" cy="12" r="10" fill="#F5A623"/>
                              <circle cx="12" cy="12" r="7" fill="#F7C948"/>
                              <ellipse cx="12" cy="12" rx="4" ry="7" stroke="#D4920B" strokeWidth="0.8" fill="none"/>
                              <line x1="5" y1="12" x2="19" y2="12" stroke="#D4920B" strokeWidth="0.8"/>
                              <line x1="12" y1="5" x2="12" y2="19" stroke="#D4920B" strokeWidth="0.8"/>
                            </>
                          ) : (
                            <>
                              <circle cx="12" cy="12" r="10" fill={planConfig.color}/>
                              <path d="M8 12.5L11 15.5L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </>
                          )}
                        </svg>
                        <span style={{ color: planConfig.color }}>{planConfig.label}</span>
                      </div>
                    );
                  })()}
                  {professional.isVerified && (
                    <div className="professional-verified-badge">
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.31291 1.49659 8.45276C1.73472 7.56231 1.02177 6.27788 1.47205 5.49856C1.92954 4.70769 3.40305 4.68605 4.04382 4.04382C4.68605 3.4016 4.70769 1.92954 5.49856 1.47205C6.27788 1.02177 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02177 16.5014 1.47205C17.2923 1.92954 17.314 3.40305 17.9562 4.04382C18.5984 4.68461 20.0705 4.70769 20.528 5.49856C20.9782 6.27788 20.2653 7.56231 20.5034 8.45276C20.7329 9.31291 22 10.0663 22 11Z" fill="#29ABE2"/>
                        <path d="M16.5876 7.60156C16.1229 7.13685 15.3696 7.13685 14.9049 7.60156L10.0312 12.4752L8.00786 10.4519C7.54315 9.98719 6.7898 9.98719 6.32509 10.4519C5.86039 10.9166 5.86039 11.6699 6.32509 12.1346L9.14798 14.9575C9.39188 15.2014 9.71372 15.3169 10.0327 15.3053C10.3516 15.3169 10.6734 15.2014 10.9173 14.9575L16.5891 9.28581C17.0538 8.82111 17.0538 8.06776 16.5891 7.60305L16.5876 7.60156Z" fill="white"/>
                      </svg>
                      <span className="verified-text">Verified</span>
                    </div>
                  )}
                </div>
                <div className="professional-rating">
                  <span className="rating-value">{professional.rating} </span>
                  <span className="rating-label">Reviews</span>
                </div>
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="professional-details">
            <div className="professional-detail-row">
              <span className="detail-label">Category:</span>
              <span className="detail-value">{professional.category}</span>
            </div>
            <div className="professional-detail-row">
              <span className="detail-label">Expertise:</span>
              <span className="detail-value">{professional.expertise}</span>
            </div>
            <div className="professional-detail-row">
              <span className="detail-label">Interested location:</span>
              <span className="detail-value">{professional.interestedLocations}</span>
            </div>
            <div className="professional-detail-row">
              <span className="detail-label">Experience:</span>
              <span className="detail-value">{professional.experience}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="professional-page">
      <Header />
      
      <main className="professional-main">
        <div className="professional-container">
          {/* Page Header Section */}
          <div className="professional-page-header">
            <h1 className="professional-page-title">
              <span className="title-main">Professional | </span>
              <span className="title-count">{totalResults.toLocaleString()} result</span>
            </h1>
            <p className="professional-page-subtitle">
              GulfConstructor connects suppliers, professionals, and contractors with live project opportunities across the region.
            </p>
          </div>

          {/* Content Section */}
          <div className="professional-content-section">
            <div className="professional-content-wrapper">
              <div className="professional-listing-section">
                {/* Breadcrumb */}
                <div className="professional-breadcrumb">
                  <span className="breadcrumb-item">Home</span>
                  <svg className="breadcrumb-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="breadcrumb-item active">Professional</span>
                </div>

                {/* Filters */}
                <div className="professional-filters">
                  <div className="filters-row">
                    <div className="filter-dropdown" onClick={() => { setShowCategoryDD(!showCategoryDD); setShowSubCategoryDD(false); setShowCountryDD(false); }} style={{position:'relative',cursor:'pointer'}}>
                      <span className="filter-label">{selectedCategory || 'Category'}</span>
                      <svg className="filter-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
                      </svg>
                      {showCategoryDD && (
                        <div style={{position:'absolute',top:'100%',left:0,background:'#fff',border:'1px solid #ddd',borderRadius:'8px',zIndex:10,minWidth:'200px',boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
                          <div style={{padding:'8px 16px',cursor:'pointer',fontSize:'14px',color:'#666'}} onClick={(e) => { e.stopPropagation(); setSelectedCategory(''); setShowCategoryDD(false); setCurrentPage(1); }}>All Categories</div>
                          {filterCategories.map(c => (
                            <div key={c} style={{padding:'8px 16px',cursor:'pointer',fontSize:'14px'}} onClick={(e) => { e.stopPropagation(); setSelectedCategory(c); setShowCategoryDD(false); setCurrentPage(1); }}>{c}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="filter-dropdown" onClick={() => { setShowSubCategoryDD(!showSubCategoryDD); setShowCategoryDD(false); setShowCountryDD(false); }}>
                      <span className="filter-label">{selectedSubCategory || 'Sub Category'}</span>
                      <svg className="filter-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
                      </svg>
                    </div>
                    <div className="filter-dropdown" onClick={() => { setShowCountryDD(!showCountryDD); setShowCategoryDD(false); setShowSubCategoryDD(false); }} style={{position:'relative',cursor:'pointer'}}>
                      <span className="filter-label">{selectedCountry || 'Country'}</span>
                      <svg className="filter-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
                      </svg>
                      {showCountryDD && (
                        <div style={{position:'absolute',top:'100%',left:0,background:'#fff',border:'1px solid #ddd',borderRadius:'8px',zIndex:10,minWidth:'200px',boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}>
                          <div style={{padding:'8px 16px',cursor:'pointer',fontSize:'14px',color:'#666'}} onClick={(e) => { e.stopPropagation(); setSelectedCountry(''); setShowCountryDD(false); setCurrentPage(1); }}>All Countries</div>
                          {filterCountries.map(c => (
                            <div key={c} style={{padding:'8px 16px',cursor:'pointer',fontSize:'14px'}} onClick={(e) => { e.stopPropagation(); setSelectedCountry(c); setShowCountryDD(false); setCurrentPage(1); }}>{c}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Professionals List */}
            <div className="professionals-list">
              {loading && <p style={{textAlign:'center',padding:'40px',color:'#666',fontSize:'16px'}}>Loading professionals...</p>}
              {!loading && professionals.length === 0 && (
                <p style={{textAlign:'center',padding:'40px',color:'#666',fontSize:'16px'}}>No professionals found. Try different filters.</p>
              )}
              {!loading && professionals.map((professional) => (
                <ProfessionalCard key={professional.id} professional={professional} />
              ))}
            </div>

            {/* Pagination */}
            <Pagination currentPage={currentPage} totalPages={Math.max(1, Math.ceil(totalResults / ITEMS_PER_PAGE))} onPageChange={setCurrentPage} />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProfessionalPage;
